import { useState, useEffect, useRef } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import RecommendedTimeSwitch from 'domains/campaigns/components/CustomCampaign/CustomCampaignForm/steps/CustomCampaignScheduleStep/RecommendedTimeSwitch';
import { getRecommendedTime } from 'domains/campaigns/components/CustomCampaign/CustomCampaignForm/steps/CustomCampaignScheduleStep/utils/getRecommendedTime';
import { Campaign } from 'domains/campaigns/types';
import { getSendDatetimeError, maxDate, maxTime, minDate, minTime } from 'domains/campaigns/utils';
import DatePicker from 'domains/core/components/DatePicker';
import TimePicker from 'domains/core/components/TimePicker';
import theme from 'theme';
import { getTimezone } from 'utils';

type WinnerScheduleStepProps = {
    campaign: Campaign;
    onScheduleChange: (scheduledTimestamp: string | null) => void;
    initialScheduledTimestamp?: string | null;
};

const WinnerScheduleStep = ({ campaign, onScheduleChange, initialScheduledTimestamp }: WinnerScheduleStepProps) => {
    const [sendDate, setSendDate] = useState<DateTime | null>(null);
    const [sendTime, setSendTime] = useState<DateTime | null>(null);
    const [isRecommendedTimeEnabled, setIsRecommendedTimeEnabled] = useState(false);
    const [isRecommendedTimeSwitchTouched, setIsRecommendedTimeSwitchTouched] = useState(false);

    // Track if we've already initialized to prevent resetting user changes
    const isInitialized = useRef(false);

    // Initialize date and time from initial scheduled timestamp (for send time AB tests)
    // Only run once on mount to avoid overwriting user changes
    useEffect(() => {
        if (initialScheduledTimestamp && !isInitialized.current) {
            const initialDateTime = DateTime.fromISO(initialScheduledTimestamp);
            if (initialDateTime.isValid) {
                setSendDate(initialDateTime);
                setSendTime(initialDateTime);
                onScheduleChange(initialDateTime.toISO());
                isInitialized.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialScheduledTimestamp]);

    // Get the winner send info
    const winnerSendId = campaign.abTestConfig?.winnerSendId;
    const winnerSend = campaign.sends.find((send) => send.id === winnerSendId);
    const winnerName = winnerSend?.name || 'New content';

    // Calculate remaining contacts
    // TODO: Fetch actual recipient count from useAudienceCount hook
    const testPercentage = campaign.abTestConfig?.testPercentage || 0;
    const remainingPercentage = 100 - testPercentage;

    const handleChangeSendDateTime = (dateTime: DateTime, isDate?: boolean) => {
        setIsRecommendedTimeSwitchTouched(true);

        // Check validity before setting state to prevent invalid values
        if (!dateTime?.isValid) {
            onScheduleChange(null);
            return;
        }

        // Set the state with valid DateTime
        isDate ? setSendDate(dateTime) : setSendTime(dateTime);

        // Use current sendDate and sendTime to build the base, or default to now
        // Ensure we only use valid DateTime objects
        const baseSendDateTime = sendDate && sendDate.isValid ? sendDate : DateTime.now();
        const baseSendTime = sendTime && sendTime.isValid ? sendTime : DateTime.now();

        // Updating sendDateTimeLuxon regardless of whether it's date or time
        // Use the incoming dateTime for the properties we're updating
        const updatedSendDateTime = baseSendDateTime.set({
            year: isDate ? dateTime.year : baseSendDateTime.year,
            month: isDate ? dateTime.month : baseSendDateTime.month,
            day: isDate ? dateTime.day : baseSendDateTime.day,
            hour: !isDate ? dateTime.hour : baseSendTime.hour,
            minute: !isDate ? dateTime.minute : baseSendTime.minute,
        });

        // Recommended time should be considered only when changing date and having recommended time enabled
        const shouldHaveRecommendedTime = isDate && isRecommendedTimeEnabled;

        // Get recommended time if necessary (recurrenceFrequency is null for non-recurring)
        const finalDateTime = shouldHaveRecommendedTime
            ? getRecommendedTime(updatedSendDateTime, null)
            : updatedSendDateTime;

        // Validate the final datetime and only update if valid
        if (finalDateTime.isValid && !getSendDatetimeError(finalDateTime, isDate)) {
            setSendDate(finalDateTime);
            setSendTime(finalDateTime);
            onScheduleChange(finalDateTime.toISO());
        }
    };

    const handleRecommendedTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsRecommendedTimeSwitchTouched(true);
        setIsRecommendedTimeEnabled(e.target.checked);

        if (e.target.checked && sendDate) {
            const newDatetime = getRecommendedTime(sendDate, null);
            setSendTime(newDatetime);
            onScheduleChange(newDatetime.toISO());
        } else if (sendDate && sendTime) {
            const currentDateTime = sendDate.set({ hour: sendTime.hour, minute: sendTime.minute });
            onScheduleChange(currentDateTime.toISO());
        }
    };

    const sendDateErrorMessage = sendDate && getSendDatetimeError(sendDate, true);
    const sendTimeErrorMessage = sendTime && getSendDatetimeError(sendTime, false);

    const scheduledTimestamp =
        sendDate && sendTime ? sendDate.set({ hour: sendTime.hour, minute: sendTime.minute }).toISO() : null;

    return (
        <Stack spacing={3}>
            <Typography variant="body1" style={{ marginBottom: '24px' }}>
                The winner '{winnerName}' will be sent to the remaining {remainingPercentage}% of your audience. Choose
                when to send your Curation.
            </Typography>

            <DatePicker
                data-testid="date-picker"
                label="Send Date"
                value={sendDate}
                minDate={minDate()}
                maxDate={maxDate()}
                onChange={(datetime) => handleChangeSendDateTime(datetime, true)}
                slotProps={{
                    textField: {
                        error: !!sendDateErrorMessage,
                        helperText: sendDateErrorMessage,
                        required: true,
                        sx: { width: '60%' },
                    },
                    popper: {
                        style: { paddingRight: theme.spacing(3.25) },
                    },
                }}
            />

            <TimePicker
                label={`Send Time (${getTimezone()})`}
                value={sendTime}
                minTime={minTime(scheduledTimestamp)}
                maxTime={maxTime(scheduledTimestamp)}
                onChange={(datetime) => handleChangeSendDateTime(datetime)}
                slotProps={{
                    textField: {
                        error: !!sendTimeErrorMessage,
                        helperText: sendTimeErrorMessage,
                        required: true,
                        sx: { width: '60%' },
                    },
                }}
                sx={{ pb: 2 }}
            />

            <Box my={-1} pl={1}>
                <RecommendedTimeSwitch
                    isChecked={isRecommendedTimeEnabled}
                    isDisabled={!!sendDateErrorMessage}
                    isTouched={isRecommendedTimeSwitchTouched}
                    recurrenceFrequency={null}
                    sendDate={sendDate}
                    onChange={handleRecommendedTimeChange}
                />
            </Box>
        </Stack>
    );
};

export default WinnerScheduleStep;
